import {useCallback, useEffect, useMemo, useState} from 'react';
import uuid from 'uuid';
import {PartialBy} from '../../../../../shared/util/type-util/PartialBy';

type Identifiable = {id: string | number | symbol};

export type NewItem<T extends Identifiable> = Omit<T, 'id'>;

export type SaveableItem<T extends Identifiable> = PartialBy<T, 'id'> & {
	_temporaryId: string; // Required so that newly created options can be edited
};

function isSaved<T extends Identifiable>(item: SaveableItem<T>): item is T & {_temporaryId: string} {
	return !!item.id;
}

function mapItemsToModifications<T extends Identifiable>(
	savedItems: readonly T[],
	existingModifications = {} as Record<T['id'], Partial<SaveableItem<T>>>
) {
	return savedItems.reduce((acc, item) => {
		return {...acc, [item.id]: existingModifications[item.id] ?? {_temporaryId: uuid.v4()}};
	}, {} as Record<T['id'], Partial<SaveableItem<T>>>);
}

// Constant empty items array to avoid possible infinite re-renders.
const EMPTY_DEFAULT_ITEMS = [];

// This hook manages the pre-saved state for a set of items. It decouples the additions, modifications, and removals from the existing set of saved items.
// This means that saved items can reload without affecting any unsaved changes in progress.
export function useSaveableItemList<T extends Identifiable>(
	savedItems: readonly T[] = EMPTY_DEFAULT_ITEMS
): {
	items: SaveableItem<T>[];
	addItem: (input: NewItem<T>) => void;
	editItem: (item: SaveableItem<T>, input: Partial<T>) => void;
	removeItem: (item: SaveableItem<T>) => void;
} {
	const [addedItems, setAddedItems] = useState<SaveableItem<T>[]>([]);
	const [modifiedSavedItems, setModifiedSavedItems] = useState(mapItemsToModifications(savedItems));

	useEffect(() => {
		setModifiedSavedItems(curr => mapItemsToModifications(savedItems, curr));
	}, [savedItems, setModifiedSavedItems]);

	const items: SaveableItem<T>[] = useMemo(
		() => [
			...savedItems
				.filter((i: T) => modifiedSavedItems[i.id]?._temporaryId)
				.map((i: T) => ({...i, ...modifiedSavedItems[i.id]})),
			...addedItems,
		],
		[savedItems, addedItems, modifiedSavedItems]
	);

	const addItem = useCallback(
		(input: NewItem<T>) => {
			setAddedItems(curr => [
				...curr,
				{
					...input,
					_temporaryId: uuid.v4(),
				},
			]);
		},
		[setAddedItems]
	);

	const editItem = useCallback(
		(item: SaveableItem<T>, input: Partial<T>) => {
			if (!isSaved(item)) {
				setAddedItems(curr => curr.map(i => (i._temporaryId === item._temporaryId ? {...i, ...input} : i)));
			} else {
				setModifiedSavedItems(curr => {
					return {
						...curr,
						[item.id]: {...curr[item.id], ...input},
					};
				});
			}
		},
		[setAddedItems, setModifiedSavedItems]
	);

	const removeItem = useCallback(
		(item: SaveableItem<T>) => {
			if (!isSaved(item)) {
				setAddedItems(curr => curr.filter(i => i._temporaryId !== item._temporaryId));
			} else {
				setModifiedSavedItems(curr => {
					return {
						...curr,
						[item.id]: {}, // Remove the temporary ID to mark this item for deletion;
					};
				});
			}
		},
		[setAddedItems, setModifiedSavedItems]
	);

	return {items, addItem, editItem, removeItem};
}
